import { i18n } from '../i18n.js';
import { MoonfallActionTrace } from '../utils/card-action-trace.js';
/**
 * UI Action Intent Module
 * Handles drag-triggered player intent choices for board cards, at any mana cost.
 */


import { FX } from './fx.js';
import { Platform } from './platform.js';
import { SoundEffects } from './sound-effects.js';
import { TutorialIntentGate } from './tutorial-intent-gate.js';
import { GameState } from '../game-state.js';
import { MoonfallGuest } from '../guest-session.js';
import { SocketHandler } from '../socket-handler.js';
import { holdRestoreSeat } from './action-intent-restore.js';
let ActionIntent = {};

ActionIntent = (function () {
    let panel = null;
    let backdrop = null;
    let isolatedBackground = [];
    let activeRequest = null;
    let lastTurn = null;
    let keyboardHandler = null;
    let returnFocusElement = null;
    let submitting = false;
    let requestGeneration = 0;
    // Request ids this client has already answered (submitted or cancelled).
    // The server keeps publishing a pending request for a short window after
    // the answer is sent — its `selectionCommitted` flag is only set once the
    // resolution actually starts — so without this a broadcast landing inside
    // that window re-opens the dialog the player just dismissed, and nothing
    // ever closes it again. A real browser run caught exactly that.
    const answeredRequestIds = new Set();
    const ANSWERED_MEMORY_LIMIT = 32;
    // card-play.js CAST_PROCESSING_GRACE_MS: the same grace for every hand play.
    const HAND_PLAY_PROCESSING_GRACE_MS = 700;

    function rememberAnswered(requestId) {
        if (!requestId) return;
        answeredRequestIds.add(requestId);
        // Bounded: a long match must not accumulate ids forever.
        if (answeredRequestIds.size > ANSWERED_MEMORY_LIMIT) {
            answeredRequestIds.delete(answeredRequestIds.values().next().value);
        }
    }
    const TITLE_ID = 'action-intent-title';
    const STATUS_ID = 'action-intent-status';
    const RESTORE_ID = 'action-intent-restore';

    function t(key, fallback) {
        if (i18n && typeof i18n.t === 'function') {
            const translated = i18n.t(key);
            return translated && translated !== key ? translated : fallback;
        }
        return fallback;
    }

    function traceRecord(traceId, eventName, details) {
        if (!traceId) return;
        MoonfallActionTrace?.record?.(traceId, eventName, details);
    }

    function traceFinish(traceId, eventName, details) {
        if (!traceId) return;
        MoonfallActionTrace?.finish?.(traceId, eventName, details);
    }

    function findFriendlyBoardCard(instanceId) {
        const state = typeof GameState !== 'undefined' && typeof GameState.get === 'function'
            ? GameState.get()
            : null;
        const cards = state && state.player && Array.isArray(state.player.summonedCards)
            ? state.player.summonedCards
            : [];
        return cards.find(card => card && card.instanceId === instanceId) || null;
    }

    function isBoardDrag(dropData) {
        return !!(
            dropData
            && (
                dropData.sourceType === 'board'
                || (typeof dropData.sourceZone === 'string' && dropData.sourceZone.startsWith('player-board-slot'))
            )
        );
    }

    /**
     * AN ANONYMOUS GAME NEVER REACHES A MODEL — so it used to never see this
     * dialog at all.
     *
     * `game/utils/aiAccessPolicy.js` states the contract: "an anonymous visitor
     * plays exactly one thing — the prerecorded tutorial — and that replay
     * serves checked-in text, actions and manga with zero model calls." The
     * LIVE intent step is a model call, so the server refuses it for a guest
     * and logs `BLOCKED storyteller action intent for anonymous game`, and the
     * refusal is not survivable as UI: the request is acknowledged `ok: true`
     * and the dialog opens BEFORE the storyteller is called, so the failure
     * arrives afterwards as `actionIntentError` and the player is stranded on
     * "Reading the field / Preparing choices...".
     *
     * That is still true of live play, and this guard still holds there. It is
     * no longer true INSIDE the recording: the tutorial's three offers are
     * authored copy served straight out of `tutorialActionIntents.json`, with
     * no storyteller in the call stack, so the step is free and a signed-out
     * visitor gets taught the mechanic instead of quietly skipping it.
     *
     * Optional-chained on purpose: `guest-session.js` reaches this module back
     * through `socket-handler.js`, so the binding is only guaranteed by the
     * time a drop happens, which is the only time this is asked.
     */
    function isAnonymousGame() {
        try { return MoonfallGuest?.isActive?.() === true; } catch (_) { return false; }
    }

    function shouldRequestForDrop(dropData) {
        if (!dropData || !dropData.instanceId || !isBoardDrag(dropData)) return false;
        /* Inside the script the tutorial decides, because it knows which of its
           steps the recording has offers for; asking on a step it does not
           cover would only earn a refusal and a dead spinner. Outside it, the
           live rules apply — including the guest block above. */
        const scriptedStep = TutorialIntentGate.isExpected();
        if (!scriptedStep && isAnonymousGame()) return false;
        const state = typeof GameState !== 'undefined' && typeof GameState.get === 'function'
            ? GameState.get()
            : null;
        if (!state || !state.isYourTurn || state.actionInProgress) return false;
        if (typeof GameState.canUseCard === 'function' && !GameState.canUseCard(dropData.instanceId)) {
            return false;
        }
        // Every commanded board Character gets the intent step — no cost gate.
        return !!findFriendlyBoardCard(dropData.instanceId);
    }

    function showNotification(message) {
        if (!message) return;
        const notification = document.createElement('div');
        notification.className = 'game-notification';
        notification.textContent = message;
        document.body.appendChild(notification);
        setTimeout(() => {
            notification.classList.add('fade-out');
            setTimeout(() => {
                if (notification.parentNode) notification.parentNode.removeChild(notification);
            }, 500);
        }, 2200);
    }

    function removeKeyboardHandler() {
        if (!keyboardHandler) return;
        document.removeEventListener('keydown', keyboardHandler, true);
        keyboardHandler = null;
    }

    function focusWithoutScroll(element) {
        if (!element || typeof element.focus !== 'function') return;
        try {
            element.focus({ preventScroll: true });
        } catch (_) {
            element.focus();
        }
    }

    // Used to disable the panel's controls while a cancel is in flight (the
    // old modal Tab trap that also read this is gone — the surface is non-modal).
    function focusableControls() {
        if (!panel) return [];
        return Array.from(panel.querySelectorAll(
            '.action-intent-option:not(:disabled), .action-intent-cancel:not(:disabled)'
        ));
    }

    function isolateBackground() {
        // Deliberately does nothing to the battle surface any more.
        //
        // This used to mark every sibling `inert` + `aria-hidden`, which is the
        // correct modal pattern but the wrong product behaviour here: `inert`
        // removes an element from hit-testing, so a player could not hover a
        // card to read it while an intent was loading or awaiting their pick.
        // Waiting on an intent should feel like waiting on a story update —
        // nothing special happens to the board. Actions are still refused, but
        // by the authoritative server, which is the only layer that has to say
        // no. Inspection is reading, not acting, and nothing needs to stop it.
        isolatedBackground = [];
    }

    function restoreBackground() {
        // Nothing is isolated now, so there is nothing to put back. Kept as the
        // teardown seam in case a future state genuinely needs isolation.
        isolatedBackground = [];
    }

    function isMobileUi() {
        try {
            return !!Platform?.isMobileUi?.();
        } catch (_) {
            return false;
        }
    }

    /**
     * Say that this intent is over — cancelled, dismissed, completed, or closed
     * by a turn boundary.
     *
     * CardDrag dims the source card while a dropped action is in flight and
     * ends the dim when that action settles. An intent that never sends one has
     * nothing to settle, so it announces its own end instead of leaving the
     * card dimmed for the rest of the match (renderBoard keeps the exact node
     * now and will not clear it). Strictly downstream and one-way: no listener
     * can change what the intent does.
     */
    function announceSettled(reason) {
        try {
            window.dispatchEvent(new CustomEvent('moonfall:action-intent-settled', {
                detail: { reason: reason || null },
            }));
        } catch (_) { /* presentation only */ }
    }

    /**
     * MINIMIZE, NOT CANCEL. A pending intent cannot be released by the player:
     * the three offers come from a fresh model call, and letting a player throw
     * them away and reopen the same action would be an unlimited reroll of an
     * authoritative decision — plus a paid generation every round. The server
     * refuses the cancel event outright; this is the affordance that replaces
     * it, so the panel is never a trap. The request stays live and untouched.
     */
    let minimized = false;
    let releaseRestoreSeat = null;

    /**
     * `focusRestore`: hand keyboard focus to the chip. Only for keyboard
     * paths (Escape, Enter/Space on Minimize) — the panel that had focus is
     * `display: none` now, so a keyboard user would otherwise be dropped on
     * `body` with the only way back unfocused. A mouse click keeps its own
     * focus behaviour: programmatic focus lights `:focus-visible` in some
     * engines, and a ring on a chip the pointer never touched reads as a bug.
     */
    function setMinimized(next, { focusRestore = false } = {}) {
        minimized = !!next;
        if (!panel) return;
        panel.classList.toggle('is-minimized', minimized);
        panel.setAttribute('aria-hidden', minimized ? 'true' : 'false');
        if (minimized) {
            if (backdrop) backdrop.classList.add('is-minimized');
            ensureRestoreButton();
            if (focusRestore) focusWithoutScroll(document.getElementById(RESTORE_ID));
        } else {
            if (backdrop) backdrop.classList.remove('is-minimized');
            removeRestoreButton();
            const firstChoice = panel.querySelector('.action-intent-option:not(:disabled)');
            if (firstChoice) firstChoice.focus();
        }
    }

    function createMinimizeButton() {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'action-intent-minimize';
        button.textContent = t('game.minimize', 'Minimize');
        // `detail` is 0 for a keyboard-activated click (Enter/Space) in every engine.
        button.addEventListener('click', (event) => setMinimized(true, { focusRestore: event.detail === 0 }));
        return button;
    }

    function ensureRestoreButton() {
        if (document.getElementById(RESTORE_ID)) return;
        const restore = document.createElement('button');
        restore.type = 'button';
        restore.id = RESTORE_ID;
        restore.className = 'action-intent-restore';
        restore.textContent = t('game.resumeIntent', 'Resume intent');
        restore.addEventListener('click', () => setMinimized(false));
        document.body.appendChild(restore);
        /* Seated from the live furniture in the SAME task that mounts it, so
           the first paint is the final position: above the hand on desktop,
           never on a card, never on End Turn or a portrait. The CSS seat is
           only the fallback for a chip this never reached. Held until the
           chip unmounts; re-seats on viewport changes only. */
        releaseRestoreSeat = holdRestoreSeat(restore);
    }

    function removeRestoreButton() {
        if (releaseRestoreSeat) {
            releaseRestoreSeat();
            releaseRestoreSeat = null;
        }
        const restore = document.getElementById(RESTORE_ID);
        if (restore && restore.parentNode) restore.parentNode.removeChild(restore);
    }

    function clear(reason = 'ui-cleared') {
        traceFinish(activeRequest?.actionTraceId, 'intent-cleared', { reason });
        removeRestoreButton();
        minimized = false;
        const hadIntent = !!(panel || activeRequest);
        const focusTarget = returnFocusElement;
        if (panel && panel.parentNode) {
            panel.parentNode.removeChild(panel);
        }
        if (backdrop && backdrop.parentNode) {
            backdrop.parentNode.removeChild(backdrop);
        }
        backdrop = null;
        restoreBackground();
        document.body.classList.remove('action-intent-active');
        panel = null;
        activeRequest = null;
        submitting = false;
        returnFocusElement = null;
        removeKeyboardHandler();
        if (focusTarget && focusTarget.isConnected) {
            focusWithoutScroll(focusTarget);
        }
        if (hadIntent) announceSettled(reason);
    }

    function ensurePanel() {
        if (panel) return panel;
        backdrop = document.createElement('div');
        backdrop.className = 'action-intent-backdrop';
        backdrop.setAttribute('aria-hidden', 'true');
        backdrop.addEventListener('pointerdown', (event) => {
            event.preventDefault();
            event.stopPropagation();
        });
        panel = document.createElement('section');
        /* A real id, not just a class. Two things need it: the tutorial's tip
           placement probes `getElementById` only (a class-named target burns
           five 500ms retries before giving up and rendering anyway), and the
           tip/skip/ornament placement solvers list blockers by id. */
        panel.id = 'action-intent-panel';
        panel.className = 'action-intent-panel';
        /* NON-modal by design. The board behind this surface stays readable
           and inspectable for the whole intent round-trip (the same owner
           decision that removed `inert` from the background), so the panel
           must never claim `aria-modal` — story-feed and assistive tech both
           treat that claim as "the rest of the page is unavailable", which is
           exactly what this product says is NOT true. Each render sets `role`
           to what the surface is right now: a status readout while loading, a
           dialog once there is a choice to make. */
        panel.setAttribute('role', 'status');
        panel.setAttribute('aria-labelledby', TITLE_ID);
        panel.setAttribute('aria-live', 'polite');
        panel.setAttribute('aria-label', t('game.actionIntentRegion', 'Action intent'));
        panel.tabIndex = -1;
        returnFocusElement = document.activeElement && document.activeElement !== document.body
            ? document.activeElement
            : null;
        document.body.appendChild(backdrop);
        document.body.appendChild(panel);
        isolateBackground();
        // Presentation only: the held-board CSS keys off this class. Input
        // ownership is nobody's — the server refuses what must be refused.
        document.body.classList.add('action-intent-active');

        keyboardHandler = (event) => {
            // Escape works from anywhere; a non-modal surface has no Tab trap.
            if (event.key !== 'Escape') return;
            if (submitting) return;
            // The board stays inspectable during an intent, so a story popup,
            // relationship stage, manga panel, or image zoom can be open ON TOP
            // of this surface. Escape belongs to that nearer layer first —
            // swallowing it here would throw away the player's intent when they
            // only meant to close a popup. The next Escape reaches us.
            const storySurfaceOpen = [
                '#story-feed-popup',
                '#story-feed-action-stage',
                '#story-feed-manga-panel',
                '#story-image-overlay',
            ].some((selector) => {
                const el = document.querySelector(selector);
                return el instanceof HTMLElement && !el.hidden && el.style.display !== 'none';
            });
            if (storySurfaceOpen) return;
            event.preventDefault();
            event.stopPropagation();
            // Escape used to CANCEL a live request, which was the same reroll
            // the button was: dismiss the offers you dislike, reopen the action,
            // draw three new ones. It minimizes now. With no live request there
            // is nothing to protect, so it still just closes.
            if (activeRequest && activeRequest.requestId) {
                setMinimized(true, { focusRestore: true });
            } else {
                clear('dismissed');
            }
        };
        document.addEventListener('keydown', keyboardHandler, true);
        return panel;
    }

    function renderLoading(title, detail, options = {}) {
        const root = ensurePanel();
        root.setAttribute('role', 'status');
        root.classList.add('is-loading');
        root.classList.remove('has-options');
        root.setAttribute('aria-busy', 'true');
        root.setAttribute('aria-describedby', STATUS_ID);
        root.innerHTML = '';

        const spinner = document.createElement('div');
        spinner.className = 'action-intent-spinner';
        spinner.setAttribute('aria-hidden', 'true');
        root.appendChild(spinner);

        const content = document.createElement('div');
        content.className = 'action-intent-copy';

        const heading = document.createElement('h2');
        heading.id = TITLE_ID;
        heading.className = 'action-intent-title';
        heading.textContent = title;
        content.appendChild(heading);

        const message = document.createElement('div');
        message.id = STATUS_ID;
        message.className = 'action-intent-status';
        message.textContent = detail;
        content.appendChild(message);
        root.appendChild(content);

        if (options.cancellable && activeRequest && activeRequest.requestId && !submitting) {
            root.appendChild(createMinimizeButton());
        }
        // No focus steal: a spinner is not a choice. The player may be reading
        // a card or a storyboard tile; the live region announces the wait.
    }

    function renderError(message) {
        const root = ensurePanel();
        root.setAttribute('role', 'dialog');
        root.classList.remove('is-loading');
        root.classList.remove('has-options');
        root.setAttribute('aria-busy', 'false');
        root.setAttribute('aria-describedby', STATUS_ID);
        root.innerHTML = '';

        const title = document.createElement('h2');
        title.id = TITLE_ID;
        title.className = 'action-intent-title';
        title.textContent = t('game.actionIntentFailed', 'Intent unavailable');
        root.appendChild(title);

        const status = document.createElement('div');
        status.id = STATUS_ID;
        status.className = 'action-intent-status';
        status.textContent = message || t('game.actionIntentFailedDetail', 'Try the action again.');
        root.appendChild(status);

        const dismiss = document.createElement('button');
        dismiss.type = 'button';
        dismiss.className = 'action-intent-cancel';
        // A failed request has nothing left to reroll, so closing it is purely
        // local — it never told the server to release anything.
        dismiss.textContent = t('game.dismiss', 'Dismiss');
        dismiss.addEventListener('click', () => { clear('dismissed'); });
        root.appendChild(dismiss);
        dismiss.focus();
    }

    function renderOptions(payload) {
        const actionTraceId = activeRequest?.actionTraceId || null;
        const options = Array.isArray(payload.options) ? payload.options.slice(0, 3) : [];
        if (options.length !== 3) {
            traceFinish(actionTraceId, 'intent-options-invalid', { optionCount: options.length });
            if (activeRequest) activeRequest.actionTraceId = null;
            renderError(t('game.actionIntentInvalid', 'The intent choices were incomplete.'));
            return false;
        }

        activeRequest = {
            requestId: payload.requestId,
            sourceCardId: payload.sourceCardId,
            targetId: payload.targetId,
            targetType: payload.targetType || null,
            targetIndex: Number.isInteger(payload.targetIndex) ? payload.targetIndex : null,
            turn: payload.turn,
            actionTraceId
        };
        traceRecord(actionTraceId, 'intent-options-rendered', { optionCount: options.length });
        lastTurn = payload.turn;

        const root = ensurePanel();
        root.setAttribute('role', 'dialog');
        root.classList.remove('is-loading');
        root.classList.add('has-options');
        root.setAttribute('aria-busy', 'false');
        root.removeAttribute('aria-describedby');
        root.innerHTML = '';

        const title = document.createElement('h2');
        title.id = TITLE_ID;
        title.className = 'action-intent-title';
        title.textContent = isMobileUi()
            ? t('game.tapIntent', 'Tap an intent')
            : t('game.chooseIntent', 'Choose intent');
        root.appendChild(title);

        /* OFFERS THE PLAYER MAY NOT TAKE. Only the recorded tutorial sends any:
           its taught turn shows three and accepts one, so the replay keeps the
           single future it has a story and a manga panel for. Disabling the
           button is a courtesy that keeps the player off a dead end — the
           commit is what actually refuses a locked pick, so a hand-written
           socket frame gains nothing by ignoring this. */
        const lockedOptionIndexes = new Set(
            (Array.isArray(payload.lockedOptionIndexes) ? payload.lockedOptionIndexes : [])
                .filter((index) => Number.isInteger(index) && index >= 0 && index < options.length)
        );
        // Every offer locked would be a trap, not a lesson. Refuse to paint it.
        if (lockedOptionIndexes.size >= options.length) lockedOptionIndexes.clear();
        /* The reason a line is closed, as a fact about the BOARD. The server
           sends it with the lock; the generic key is only a fallback, and it
           must never be the thing a player reads in the tutorial — nothing may
           hint that the match is anything but live. */
        const lockedReason = typeof payload.lockedReason === 'string' && payload.lockedReason.trim()
            ? payload.lockedReason.trim()
            : t('game.actionIntentLocked', 'Not from here');

        const list = document.createElement('div');
        list.className = 'action-intent-options';
        options.forEach((option, index) => {
            const button = document.createElement('button');
            button.type = 'button';
            // Discover presentation: tiles deal in staggered; picking one
            // bursts + chimes before the selection submits.
            button.className = 'action-intent-option action-intent-option--deal';
            /* STABLE IDENTITY, or the tutorial can only ever cue one of them.
               The cue layer keys an anchor on `data-instance-id || data-card-id
               || id`, and three id-less buttons all answer 'anon' — three cues
               collapse into one map entry. It is also how a cue survives this
               function's `innerHTML = ''` re-render: `resolveAnchor` re-finds a
               non-card anchor by id. */
            button.id = `action-intent-option-${index}`;
            button.dataset.optionIndex = String(index);
            /* The pick's sound is the chime below. The shared press cue
               (press-sound.js) ticks every <button>, so without this a pick
               landed as a tick UNDER the chime — two cues for one press. The
               opt-out means ownership: this tile plays its own feedback. */
            button.dataset.pressSound = 'off';
            button.style.setProperty('--option-delay', `${index * 80}ms`);
            button.textContent = option;
            if (lockedOptionIndexes.has(index)) {
                button.classList.add('action-intent-option--locked');
                button.disabled = true;
                // `disabled` already conveys "unavailable"; this says WHY, and
                // a `title` would never reach a touch device or a screen
                // reader on a disabled control.
                const why = document.createElement('span');
                why.className = 'action-intent-option-lock';
                why.textContent = lockedReason;
                button.appendChild(why);
            }
            button.addEventListener('click', () => {
                FX?.burst?.(button);
                SoundEffects?.play?.('magic');
                // submit() has already rendered any failure state. Consume the
                // returned rejection here so a network/server failure from a
                // user click never becomes an unhandled promise rejection.
                void submit(index).catch(() => {});
            });
            list.appendChild(button);
        });
        root.appendChild(list);

        root.appendChild(createMinimizeButton());

        /* The first TAKEABLE offer. `focus()` on a disabled button is a no-op,
           so seeking `.action-intent-option` blindly would leave the tutorial's
           taught turn with no focus at all and nothing for a keyboard to act
           on — that turn locks index 0, and its open offer is index 1. */
        const firstChoice = root.querySelector('.action-intent-option:not(:disabled)');
        if (firstChoice) firstChoice.focus();
        /* Downstream, presentation-only: the tutorial cue layer anchors its
           ring and its coach mark to the offer the player is meant to take.
           It cannot read this panel's private state, and nothing it does can
           change the offers — see `moonfall:action-intent-settled` for the
           other half of the pair. */
        try {
            window.dispatchEvent(new CustomEvent('moonfall:action-intent-options', {
                detail: {
                    requestId: payload.requestId || null,
                    // The card this dialog is asking about. The tutorial keeps
                    // it lit while the rest of the board dims, so the player
                    // can see WHICH character the question belongs to.
                    sourceCardId: payload.sourceCardId || null,
                    optionCount: options.length,
                    lockedOptionIndexes: [...lockedOptionIndexes],
                },
            }));
        } catch (_) { /* presentation only */ }
        return true;
    }

    function request(sourceCardId, targetType, targetId, targetIndex, actionTraceId) {
        if (!sourceCardId) return Promise.reject(new Error('Missing source card.'));
        const generation = ++requestGeneration;
        const traceId = actionTraceId
            || MoonfallActionTrace?.findOpen?.(sourceCardId)
            || null;
        activeRequest = {
            sourceCardId,
            targetType: targetType || null,
            targetId,
            targetIndex: Number.isInteger(targetIndex) ? targetIndex : null,
            actionTraceId: traceId,
            requestGeneration: generation
        };
        traceRecord(traceId, 'intent-requested', {
            targetType: targetType || null,
            targetId: targetId || null,
            targetIndex: Number.isInteger(targetIndex) ? targetIndex : null,
        });
        renderLoading(
            t('game.actionIntentLoading', 'Reading the field'),
            t('game.actionIntentLoadingDetail', 'Preparing choices...')
        );

        if (typeof SocketHandler === 'undefined' || typeof SocketHandler.requestActionIntentOptions !== 'function') {
            const err = new Error('Action intent is unavailable.');
            handleError(err.message);
            return Promise.reject(err);
        }

        return SocketHandler.requestActionIntentOptions(sourceCardId, targetType, targetId, targetIndex)
            .then((response) => {
                if (!activeRequest || activeRequest.requestGeneration !== generation) {
                    traceFinish(traceId, 'intent-request-superseded', {
                        requestCreated: !!response?.requestId,
                    });
                    return response;
                }
                traceRecord(traceId, 'intent-request-accepted', {
                    requestCreated: !!response?.requestId,
                });
                if (activeRequest && response && response.requestId) {
                    activeRequest.requestId = response.requestId;
                    renderLoading(
                        t('game.actionIntentLoading', 'Reading the field'),
                        t('game.actionIntentLoadingDetail', 'Preparing choices...'),
                        { cancellable: true }
                    );
                }
                return response;
            })
            .catch((err) => {
                traceFinish(traceId, 'intent-request-error', {
                    message: err && err.message ? err.message : 'Intent request failed',
                });
                if (activeRequest && activeRequest.requestGeneration === generation) {
                    handleError(err && err.message ? err.message : t('game.actionIntentFailedDetail', 'Try the action again.'));
                }
                throw err;
            });
    }

    function routeOrPlay(dropData, targetType, targetId, targetIndex) {
        if (shouldRequestForDrop(dropData)) {
            return request(
                dropData.instanceId,
                targetType,
                targetId,
                targetIndex,
                dropData.actionTraceId,
            );
        }
        traceRecord(dropData?.actionTraceId, 'direct-action-route', {
            targetType: targetType || null,
            targetId: targetId || null,
        });
        if (typeof SocketHandler !== 'undefined' && typeof SocketHandler.playAction === 'function') {
            /* A card played FROM THE HAND is spent on its target as it is let
               go (drag to play); the centred processing chrome waits out a
               grace so it never lands on that moment. Presentation only. */
            if (dropData?.sourceType === 'hand') {
                return SocketHandler.playAction(
                    dropData.instanceId,
                    targetType,
                    targetId,
                    Number.isInteger(targetIndex) ? targetIndex : undefined,
                    { processingGraceMs: HAND_PLAY_PROCESSING_GRACE_MS },
                );
            }
            if (Number.isInteger(targetIndex)) {
                return SocketHandler.playAction(dropData.instanceId, targetType, targetId, targetIndex);
            }
            return SocketHandler.playAction(dropData.instanceId, targetType, targetId);
        }
        traceFinish(dropData?.actionTraceId, 'client-blocked', {
            reason: 'action-handler-unavailable',
        });
        return Promise.reject(new Error('Action handler is unavailable.'));
    }

    function handleOptions(payload) {
        if (!payload) return false;
        if (!activeRequest || !activeRequest.requestId || payload.requestId !== activeRequest.requestId) {
            return false;
        }
        return renderOptions(payload);
    }

    function handleError(message, requestId = null) {
        if (requestId && (!activeRequest || activeRequest.requestId !== requestId)) {
            return false;
        }
        traceFinish(activeRequest?.actionTraceId, 'intent-error', { message });
        submitting = false;
        activeRequest = null;
        renderError(message);
        showNotification(message);
        // The panel stays open on its Dismiss button, but the action is dead:
        // nothing more will be sent for it, so its source card goes back to rest.
        announceSettled('error');
        return true;
    }

    function submit(optionIndex) {
        if (!activeRequest || !activeRequest.requestId || submitting) return Promise.resolve();
        const selectionRequest = activeRequest;
        const requestId = selectionRequest.requestId;
        const actionTraceId = selectionRequest.actionTraceId || null;
        traceRecord(actionTraceId, 'intent-selection-submitted', { optionIndex });
        submitting = true;
        renderLoading(
            t('game.actionIntentResolving', 'Resolving intent'),
            t('game.actionIntentResolvingDetail', 'Updating the battle...')
        );

        if (typeof SocketHandler === 'undefined' || typeof SocketHandler.submitActionIntentSelection !== 'function') {
            const err = new Error('Action intent is unavailable.');
            handleError(err.message);
            return Promise.reject(err);
        }

        rememberAnswered(requestId);
        return SocketHandler.submitActionIntentSelection(requestId, optionIndex)
            .then(() => {
                traceFinish(actionTraceId, 'action-complete', { route: 'intent-selection' });
                // A turn transition can clear this dialog and open a new one
                // before the old acknowledgement reaches the browser. Never
                // let that late completion tear down the newer request.
                if (activeRequest !== selectionRequest) return;
                activeRequest.actionTraceId = null;
                clear('completed');
            })
            .catch((err) => {
                traceFinish(actionTraceId, 'intent-selection-error', {
                    message: err && err.message ? err.message : 'Intent selection failed',
                });
                if (activeRequest === selectionRequest) {
                    handleError(err && err.message ? err.message : t('game.actionIntentFailedDetail', 'Try the action again.'));
                }
                throw err;
            });
    }


    /**
     * True while a server-issued request is still awaiting this client's answer.
     * The server keeps `actionInProgress` set for exactly this window, so
     * anything that tears down UI must consult this before touching the dialog.
     */
    function hasPendingRequest() {
        return !!activeRequest?.requestId;
    }

    /**
     * Rebuild the dialog from the authoritative player view.
     *
     * The server publishes its own pending request as `gameState.actionIntent`.
     * Any client that lost the dialog — tab switch, reload, a phone killing the
     * backgrounded tab, a socket reconnect — recovers here instead of stranding
     * the player on a board they are no longer allowed to touch.
     */
    function restoreFromGameState(state) {
        const pending = state?.actionIntent;
        if (!pending?.requestId || !Array.isArray(pending.options)) return false;
        // A request from an earlier turn is already dead server-side; restoring
        // it would only offer the player a choice that cannot be committed.
        if (Number.isInteger(state.currentTurn) && pending.turn !== state.currentTurn) return false;
        // Already answered — the server just has not caught up yet.
        if (answeredRequestIds.has(pending.requestId)) return false;
        if (activeRequest?.requestId === pending.requestId && panel) return false;
        // Rendering is not enough: the socket layer tracks a per-action owner
        // used to correlate the reply, and a recovered dialog has none. Without
        // adopting it the buttons render and then refuse with "Action intent
        // request is no longer active" — a dialog that looks fine and does
        // nothing, which is barely better than the deadlock this replaces.
        if (typeof SocketHandler?.adoptPendingActionIntent === 'function'
            && !SocketHandler.adoptPendingActionIntent(pending.requestId)) {
            return false;
        }
        if (panel) clear('superseded-by-authoritative-state');
        return renderOptions(pending);
    }

    function syncTurn(turnNumber) {
        if (lastTurn !== null && turnNumber !== lastTurn) {
            clear('turn-changed');
        }
        lastTurn = turnNumber;
    }

    return {
        request,
        routeOrPlay,
        handleOptions,
        handleError,
        syncTurn,
        clear,
        markAnswered: rememberAnswered,
        hasPendingRequest,
        restoreFromGameState,
        shouldRequestForDrop
    };
})();

export { ActionIntent };
